import { useEffect, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { AtwLogo } from "@/components/AtwLogo";
import { useAuth } from "@/lib/auth";
import {
  ATW_MAILING_ADDRESS,
  ATW_TERMS_URL,
  LEGAL_DBA,
  LEGAL_ENTITY,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_HREF,
} from "@/lib/publicSite";

type PublicLayoutProps = {
  title: string;
  children: ReactNode;
};

const NAV_LINKS = [
  { to: "/support", label: "Support" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
  { to: "/security", label: "Security" },
  { to: "/docs/slack", label: "Guides" },
] as const;

function navLinkClass({ isActive }: { isActive: boolean }) {
  return isActive ? "is-active" : undefined;
}

/** Chrome for pages anyone can open without signing in. */
export function PublicLayout({ title, children }: PublicLayoutProps) {
  const { user } = useAuth();
  const hubTarget = user ? "/" : "/login";
  const hubLabel = user ? "Open Hub" : "Sign in";
  const year = new Date().getFullYear();

  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="public-page">
      <a href="#public-content" className="public-skip">
        Skip to content
      </a>
      <header className="public-header">
        <div className="public-header-bar">
          <Link to="/support" className="public-brand">
            <AtwLogo size={32} label="" />
            <span>ATW Hub</span>
          </Link>
          <Link to={hubTarget} className="public-header-cta">
            {hubLabel}
          </Link>
        </div>
        <nav className="public-nav" aria-label="Site">
          <div className="public-nav-scroll">
            {NAV_LINKS.map((item) => (
              <NavLink key={item.to} to={item.to} className={navLinkClass}>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <main id="public-content" className="public-main">
        {children}
      </main>
      <footer className="public-footer">
        <div className="public-footer-grid">
          <div className="public-footer-col">
            <h2 className="public-footer-heading">Hub</h2>
            <ul>
              <li>
                <Link to="/support">Support</Link>
              </li>
              <li>
                <Link to="/security">Security</Link>
              </li>
              <li>
                <Link to={hubTarget}>{hubLabel}</Link>
              </li>
            </ul>
          </div>
          <div className="public-footer-col">
            <h2 className="public-footer-heading">Policies</h2>
            <ul>
              <li>
                <Link to="/privacy">Privacy</Link>
              </li>
              <li>
                <Link to="/terms">Terms</Link>
              </li>
              <li>
                <a href={ATW_TERMS_URL}>{LEGAL_DBA} terms (atw.net)</a>
              </li>
            </ul>
          </div>
          <div className="public-footer-col">
            <h2 className="public-footer-heading">Guides</h2>
            <ul>
              <li>
                <Link to="/docs/slack">Slack</Link>
              </li>
              <li>
                <Link to="/docs/zoom">Zoom</Link>
              </li>
              <li>
                <Link to="/docs/teams">Teams</Link>
              </li>
              <li>
                <Link to="/slack">Slack app page</Link>
              </li>
            </ul>
          </div>
          <div className="public-footer-col">
            <h2 className="public-footer-heading">Contact</h2>
            <ul>
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </li>
              <li>
                <a href={SUPPORT_PHONE_HREF}>{SUPPORT_PHONE}</a>
              </li>
              <li>{ATW_MAILING_ADDRESS}</li>
            </ul>
          </div>
        </div>
        <p className="public-footer-copy">
          © {year} {LEGAL_ENTITY}, doing business as {LEGAL_DBA}. University Heights, Ohio.
        </p>
      </footer>
    </div>
  );
}
